import {TextInput} from '@volkswagen-onehub/components-core';
import {debounce} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import DropdownInput, {OptionItem} from '../components/DropdownInput';
import QuestionContentWrapper from '../components/QuestionContentWrapper';
import InputWrapperWithLabel from '../components/StyledInputWrapperWithLabel';
import useQuestionStore from '../store/navigation';
import {QUESTIONIDS, Question} from '../types/question';
import {useResultStore} from '../store/responses';
import {config} from '../environment';
import {sendAnalyticPayload} from '../utils/analytic';
import {QuestionData, QuestionStepName} from '../constants/questions';
import useMediaQuery from '../hooks/useMediaQuery';

export interface IPostCodeData {
  label: string;
  state: string;
  locality: string;
  postcode: string;
}

function QuestionNineAddress(props: Question) {
  const {nextStepId, title} = props;
  const {updateQuestion} = useResultStore();
  const {setCurrentQuestion, currentQuestion} = useQuestionStore();
  const [showPanel, setShowPanel] = useState(false);
  const [value, setValue] = useState('');
  const [listPostCode, setListPostCode] = useState<OptionItem[]>([]);
  const [message, setMessage] = useState('');
  const [nextButtonAvailable, setNextButtonAvailable] = useState(false);
  const [loading, setLoading] = React.useState(false);
  const [valuePostCode, setValuePostCode] = useState('');
  const responseDataRef = useRef<IPostCodeData[]>([]);
  const {isMobile} = useMediaQuery();

  const fetchState = async (value: string) => {
    if (value?.length > 1) {
      try {
        setLoading(true);
        const localitySearchResponse = await fetch(
          `${config.API_VW_LOCALITY_SEARCH_URL}`,
          {
            method: 'POST',
            body: JSON.stringify({
              locality: value || '',
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        const response = await localitySearchResponse.json();
        const responseData: IPostCodeData[] = response.data;
        if (responseData?.length > 0) {
          // Save the response data to a ref, so we can use it later
          responseDataRef.current = responseData;
          setMessage('');
          setListPostCode(
            [...responseData]
              .filter((item) =>
                item.label
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase()),
              )
              .map((item) => {
                return {
                  id: item.postcode,
                  label: item.label,
                  value: item.locality,
                };
              }),
          );
        } else {
          setMessage('Please enter a valid Australian address');
          setListPostCode([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
  };
  const debounceSearch = useRef(
    debounce((value) => fetchState(value), 400),
  ).current;

  const dropdownMaxHeight =
    listPostCode.length * 42 > 200 ? 200 : listPostCode.length * 43;

  const handleSelectOption = (option: OptionItem) => {
    setValue(option.label);
    setShowPanel(false);
    setNextButtonAvailable(true);
    setValuePostCode(option.id);
  };

  const handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement;
    const inputValue = target?.value;
    setValue(inputValue);
    if (inputValue.length >= 2) setShowPanel(true);
    else setShowPanel(false);
    setNextButtonAvailable(false);
    debounceSearch(inputValue);
  };
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showPanel &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowPanel(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showPanel]);
  const handleNextButton = () => {
    const data = responseDataRef.current.find(
      (item) => item.postcode === valuePostCode,
    );

    if (!data) return;

    updateQuestion({
      id: currentQuestion as typeof QUESTIONIDS.questionEightAddress,
      value: data,
    });

    setCurrentQuestion(nextStepId);

    const stepName = QuestionStepName[currentQuestion] || currentQuestion;
    sendAnalyticPayload({
      process: {
        StepName: stepName,
        StepNumber: QuestionData[currentQuestion]?.progressValue.toString(),
      },
      question: [
        {
          Question: stepName,
          Answer: [
            {
              value: data.label,
              type: 'input_text',
            },
          ],
        },
      ],
    });
  };
  return (
    <QuestionContentWrapper
      title={title}
      disabled={!nextButtonAvailable}
      handleClick={handleNextButton}
    >
      <div
        style={{
          position: 'relative',
          minHeight: 200,
          ...(isMobile && {width: '100%'}),
        }}
        ref={dropdownRef}
      >
        <form>
          <InputWrapperWithLabel>
            <TextInput
              label="Postcode or suburb"
              value={value}
              onChange={(event) => handleChange(event)}
              hasClearButton={!!value}
              isClearButtonVisible
              onClear={() => {
                setValue('');
                setNextButtonAvailable(false);
              }}
              clearIconLabel="Clear search"
              placeholder="Please enter your postcode"
              onFocus={() => {
                if (value?.length >= 2) setShowPanel(true);
              }}
            />
          </InputWrapperWithLabel>
          {showPanel && (
            <DropdownInput
              loading={loading}
              options={listPostCode}
              handleOptionChange={handleSelectOption}
              dropdownMaxHeight={dropdownMaxHeight}
              message={message || 'Please enter a valid Australian address'}
            />
          )}
        </form>
      </div>
    </QuestionContentWrapper>
  );
}

export default QuestionNineAddress;
